import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  Heading,
  Content,
  Footer,
  Text,
  ButtonGroup,
  Link,
  LinkButton,
  Badge,
  ActionButtonGroup,
  ActionButton,
  IllustratedMessage,
  ContextualHelp,
} from "@react-spectrum/s2";
import PropTypes from "prop-types";

import AssetsIllustration from "@react-spectrum/s2/illustrations/gradient/generic2/Assets";
import ClockIcon from "@react-spectrum/s2/illustrations/linear/Clock";
import AIGenerateIcon from "icon:@app/assets/AIGenerate.svg";
import FigmaIcon from "icon:@app/assets/S2_Figma.svg";

import {
  useVideos,
  useVideoTemplates,
  getNewVideoPath,
} from "@app/hooks/videos";

import { ProjectsList } from "./projects/ProjectsList";
import { TemplatesList } from "./projects/new/TemplatesList";
import { AccountDetailsStep } from "./projects/new/AccountDetailsStep";
import { ModularPlaceholderCard } from "./projects/new/ModularPlaceholderCard";

import { size, style } from "@react-spectrum/s2/style" with { type: "macro" };

const RECENT_VIDEOS_LIMIT = 6;

export const WelcomeView = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const videos = useVideos({ pageSize: RECENT_VIDEOS_LIMIT });

  const defaultTemplates = useVideoTemplates({ type: ["express", "mogrt"] });
  const modularTemplates = useVideoTemplates({ type: "modular" });
  const htmlTemplates = useVideoTemplates({ type: "html" });

  // The account-details flow is entirely URL-driven so it survives refreshes and
  // can be deep-linked (e.g. `/?template=<id>&flow=video&sfType=accountName`).
  const selectedTemplateKey = searchParams.get("template");
  const flow = searchParams.get("flow");
  const sfType = searchParams.get("sfType") ?? "accountName";
  const accountName = searchParams.get("accountName") ?? "";
  const drNumber = searchParams.get("drNumber") ?? "";
  const isAccountDialogOpen = !!selectedTemplateKey;

  const patchParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value == null || value === "") {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleCardPress = useCallback(
    ({ id, videoPath, templateId }) => {
      if (!videoPath) {
        navigate(getNewVideoPath(id));
        return;
      }

      navigate(`/projects/${id}`);
    },
    [navigate],
  );

  // Selecting a template opens the account-details dialog in place (no redirect),
  // recording the choice in the URL. `flow` tracks which wizard to continue to.
  const handleTemplateSelect = useCallback(
    (flowType) => (keys) => {
      const [templateKey] = keys;
      if (!templateKey) {
        return;
      }
      patchParams({
        template: templateKey,
        flow: flowType,
        sfType: "accountName",
        accountName: null,
        drNumber: null,
      });
    },
    [patchParams],
  );

  const handleAccountDialogCancel = useCallback(() => {
    patchParams({
      template: null,
      flow: null,
      sfType: null,
      accountName: null,
      drNumber: null,
    });
  }, [patchParams]);

  // Hand the collected template + account details off to the wizard as query
  // params so it can start past template selection (and account details).
  const handleAccountDialogContinue = useCallback(() => {
    let path;

    switch (flow) {
      case "modular":
        path = "/projects/composer";
        break;
      case "story":
        path = "/stories/new";
        break;
      default:
        path = "/projects/new";
        break;
    }

    const params = new URLSearchParams();
    params.set("template", selectedTemplateKey);
    params.set("sfType", sfType);
    if (accountName) {
      params.set("accountName", accountName);
    }
    if (drNumber) {
      params.set("drNumber", drNumber);
    }
    navigate(`${path}?${params.toString()}`);
  }, [navigate, flow, selectedTemplateKey, sfType, accountName, drNumber]);

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        paddingY: 80,
        paddingX: 24,
        marginY: "auto",
        boxSizing: "border-box",
        minHeight: "full",
      })}
    >
      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginX: 24,
          alignItems: "center",
          textAlign: "center",
        })}
      >
        <Heading
          level={1}
          styles={style({
            font: {
              default: "heading-xl",
              md: "heading-2xl",
            },
            lineHeight: "[1]",
            marginTop: 0,
            marginBottom: 24,
          })}
        >
          <Text
            styles={style({
              display: "block",
              alignItems: "center",
              gap: 8,
            })}
          >
            Create video sizzle reels for your customers – in minutes.
          </Text>
        </Heading>
      </div>

      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 96,
          width: "full",
          maxWidth: 1600,
          marginX: "auto",
          marginTop: 80,
          paddingBottom: 48,
        })}
      >
        <div
          className={style({
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            flexShrink: 1,
            maxWidth: "full",
            marginX: "auto",
            gap: 16,
          })}
        >
          <Heading styles={style({ font: "heading-lg", marginY: 8 })}>
            Start from the template
            <wbr />
            <Text styles={style({ font: "ui-xl", marginStart: 12 })}>
              that best fits your message
            </Text>
          </Heading>

          <TemplatesList
            templates={defaultTemplates}
            emptyStateMessage="No templates found"
            selectedTemplateKey={
              flow === "modular" ? null : selectedTemplateKey
            }
            onSelectionChange={handleTemplateSelect("video")}
            orientation="horizontal"
            showScrollArrows
          />
        </div>

        <div
          className={style({
            display: "flex",
            flexDirection: {
              default: "column-reverse",
              sm: "row",
            },
            alignItems: "start",
            minWidth: 0,
            flexShrink: 1,
            maxWidth: "full",
            marginX: "auto",
            gap: 48,
          })}
        >
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flexShrink: 1,
              maxWidth: "full",
              marginX: "auto",
              gap: 16,
            })}
          >
            <div
              className={style({
                display: "flex",
                alignItems: "center",
                gap: 8,
              })}
            >
              <ContextualHelp variant="info" size="S" placement="top start">
                <Heading>
                  <Text>Video Composer</Text>
                  <Badge
                    size="XS"
                    variant="yellow"
                    styles={style({ marginStart: 8 })}
                    UNSAFE_className={style({
                      verticalAlign: "text-top",
                      borderRadius: "sm",
                    })}
                    UNSAFE_style={{
                      paddingInlineStart: size(6),
                      paddingInlineEnd: size(6),
                      display: "inline-flex",
                    }}
                  >
                    Alpha
                  </Badge>
                </Heading>
                <Content>
                  <Text>
                    Brand new script, unique video. Made for your Customer.
                  </Text>
                </Content>
                <Footer>
                  <Text>
                    It&apos;s experimental — try it, break it, shape what&apos;s
                    next.
                  </Text>
                  <ActionButtonGroup
                    orientation="vertical"
                    styles={style({ marginTop: 16 })}
                  >
                    <Link
                      href="https://f.io/0NSot_uj"
                      target="_blank"
                      variant="secondary"
                      isStandalone
                    >
                      Watch demo video
                    </Link>
                    <Link
                      href="https://f.io/TxN9A3Q4"
                      target="_blank"
                      variant="secondary"
                      isStandalone
                    >
                      See output examples
                    </Link>
                    <Link
                      href="slack://channel?team=E23RE8G4F&id=C095CSV0N0Z"
                      target="_blank"
                      variant="secondary"
                      isStandalone
                    >
                      Share your thoughts
                    </Link>
                  </ActionButtonGroup>
                </Footer>
              </ContextualHelp>
              <Heading
                styles={style({
                  display: "inline-flex",
                  flexDirection: {
                    default: "column",
                    sm: "row",
                  },
                  gap: 0,
                  alignItems: "baseline",
                  font: "heading-lg",
                  marginY: 8,
                })}
              >
                <Text styles={style({ whiteSpace: "nowrap" })}>
                  <Text>Video Composer</Text>
                  <Badge
                    size="XS"
                    variant="yellow"
                    styles={style({ marginStart: 8 })}
                    UNSAFE_className={style({
                      verticalAlign: "text-top",
                      borderRadius: "sm",
                    })}
                    UNSAFE_style={{
                      paddingInlineStart: size(6),
                      paddingInlineEnd: size(6),
                      display: "inline-flex",
                    }}
                  >
                    Alpha
                  </Badge>
                </Text>
                <Text styles={style({ font: "ui-xl", marginStart: 12 })}>
                  try out what&apos;s coming next and{" "}
                  <Link
                    href="slack://channel?team=E23RE8G4F&id=C095CSV0N0Z"
                    target="_blank"
                    variant="secondary"
                  >
                    share your feedback
                  </Link>
                </Text>
              </Heading>
            </div>

            <div
              className={style({
                boxSizing: "border-box",
                overflow: "hidden",
                width: {
                  lg: "fit",
                  default: "auto",
                },
                maxWidth: {
                  lg: "full",
                  default: `calc(100% + ${size(64)})`,
                },
                paddingTop: 24,
                paddingX: 24,
                borderRadius: "xl",
                backgroundColor: "transparent-overlay-50",
                borderWidth: 1,
                borderColor: "transparent-overlay-100",
                borderStyle: "solid",
                marginX: {
                  lg: 0,
                  default: -32,
                },
              })}
            >
              <TemplatesList
                templates={modularTemplates}
                emptyStateMessage="No templates found"
                selectedTemplateKey={
                  flow === "modular" ? selectedTemplateKey : null
                }
                onSelectionChange={handleTemplateSelect("modular")}
                placeholder={<ModularPlaceholderCard />}
                orientation="horizontal"
              />
            </div>
          </div>

          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              // minWidth: 0,
              flexShrink: 1,
              maxWidth: "full",
              marginX: "auto",
              gap: 16,
            })}
          >
            <Heading
              styles={style({
                display: "flex",
                flexDirection: "column",
                font: "heading-lg",
                marginY: 8,
              })}
            >
              <Text
                styles={style({
                  display: "inline-flex",
                  alignItems: "start",
                  // gap: 4,
                  font: "ui-xl",
                  marginEnd: 12,
                })}
              >
                Try out the new <FigmaIcon styles={style({ marginStart: 4 })} />{" "}
                Figma
              </Text>
              <wbr />
              One Adobe template
            </Heading>

            <TemplatesList
              templates={htmlTemplates}
              emptyStateMessage={
                <div
                  className={style({
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  })}
                >
                  <Text>Coming soon!</Text>
                  <Text styles={style({ font: "ui" })}>
                    Join{" "}
                    <Link
                      href="slack://channel?team=E23RE8G4F&id=C095CSV0N0Z"
                      target="_blank"
                      variant="secondary"
                    >
                      #bestory-community
                    </Link>{" "}
                    to get notified!
                  </Text>
                </div>
              }
              emptyStateIcon={<ClockIcon />}
              emptyStateSize="S"
              selectedTemplateKey={null}
              onSelectionChange={handleTemplateSelect("story")}
              orientation="vertical"
            />
          </div>
        </div>

        <div
          className={style({
            display: "flex",
            flexDirection: "column",
            gap: 8,
            width: "full",
            maxWidth: 1000,
            marginX: "auto",
          })}
        >
          <div
            className={style({
              display: "flex",
              alignItems: "center",
              gap: 12,
            })}
          >
            <Heading styles={style({ font: "title-lg", marginY: 0 })}>
              Recent projects
            </Heading>
            {!!videos.data?.length && (
              <ActionButton
                textValue="All projects"
                isQuiet
                onPress={() => navigate("/projects")}
              >
                View all
              </ActionButton>
            )}
          </div>

          <div className={style({ height: 260 })}>
            <ProjectsList
              limit={RECENT_VIDEOS_LIMIT}
              selectionMode="single"
              onCardPress={handleCardPress}
              renderEmptyState={() =>
                videos.isSuccess ? (
                  <IllustratedMessage size="S">
                    <AssetsIllustration />
                    <Heading>Nothing Created So Far</Heading>
                    <Content>
                      You haven&apos;t made any projects yet—start your first
                      one now!
                    </Content>
                  </IllustratedMessage>
                ) : null
              }
              styles={style({
                boxSizing: "border-box",
                marginX: -12,
              })}
            />
          </div>
        </div>
      </div>

      <AccountDetailsStep
        isOpen={isAccountDialogOpen}
        sfType={sfType}
        onSfTypeChange={(value) =>
          patchParams({ sfType: value, accountName: null, drNumber: null })
        }
        accountName={accountName}
        onAccountNameChange={(value) => patchParams({ accountName: value })}
        onCancel={handleAccountDialogCancel}
        onContinue={handleAccountDialogContinue}
      />

      {children}
    </div>
  );
};

WelcomeView.propTypes = {
  children: PropTypes.node,
};
